import moment from 'moment';
import React, { useEffect, useMemo, useState } from 'react'
import "./GroupedEarnings.scss";
import { formatAmount } from '../../utils/helpers';
import ArrowDownIcon from '../../assets/icons/ArrowDownIcon';
import ChevronDownIcon from '../../assets/icons/ChevronDownIcon';
import ChevronUpIcon from '../../assets/icons/ChevronUpIcon';
import { AppDispatch, RootState } from '../../redux-stores/store';
import { useDispatch, useSelector } from 'react-redux';
import { fetchConversionCampaignGroupedEarningsThunk } from '../../redux-stores/slices/conversionCampaignSlice';
import Skeleton from 'react-loading-skeleton';
type RawItem = {
    month: string;
    status: string;
    total_amount: string;
};

const FIRST_VIEW_EXPANDED_COUNT = 1;
const COLLAPSED_ITEMS_COUNT = 3;

const GroupedEarnings = () => {

    const dispatch = useDispatch<AppDispatch>();

    const { data, loading, currency } = useSelector((state: RootState) => state.conversionCampaigns.earnings);

    useEffect(() => {
        dispatch(fetchConversionCampaignGroupedEarningsThunk({}));
    }, [dispatch]);

    const [showAll, setShowAll] = useState(false);

    const rawItems: RawItem[] = data ?? [];

    const groupedItems = useMemo(() => {
        let grouped: Record<string, any> = {};
        for (let item of rawItems) {
            let key = item.month;
            let dateObj = new Date(key);
            if (!grouped[key]) {
                grouped[key] = {
                    key: key,
                    date: dateObj,
                    label: moment(dateObj).format('MMMM YYYY'),
                    data: {
                        [item.status]: item.total_amount
                    }
                }
            } else {
                grouped[key].data[item.status] = item.total_amount;
            }
        }
        return grouped;
    }, [rawItems])

    const sortedGroupedItems = useMemo(() => {
        return Object.values(groupedItems).sort((a, b) => b.date.getTime() - a.date.getTime());
    }, [groupedItems])


    const firstViewExpandedItems = useMemo(() => {
        return sortedGroupedItems.slice(0, FIRST_VIEW_EXPANDED_COUNT);
    }, [sortedGroupedItems])

    const remainingItemsCount = useMemo(() => {
        return sortedGroupedItems.length - FIRST_VIEW_EXPANDED_COUNT;
    }, [sortedGroupedItems])

    const remainingItems = useMemo(() => {
        return sortedGroupedItems.slice(FIRST_VIEW_EXPANDED_COUNT, FIRST_VIEW_EXPANDED_COUNT + COLLAPSED_ITEMS_COUNT);
    }, [sortedGroupedItems, remainingItemsCount])

    return (
        <div className='grouped-earnings mb-3'>
            <div className="grouped-earnings__first-view-expanded">
                {
                    loading ? (
                        <Skeleton height={100} width={'100%'} count={FIRST_VIEW_EXPANDED_COUNT} borderRadius={'1rem'} />
                    ) : (
                        <>
                            {
                                (showAll ? sortedGroupedItems : firstViewExpandedItems).map((item) => {
                                    return (
                                        <div className="grouped-earning__card app-card p-075" key={item.key}>
                                            <span className="text-dark fs-16 fw-400">{item.label}</span>
                                            <div className="grouped-earning__card-content">
                                                <div className="grouped-earning__card-content-item app-sub-card">
                                                    <span className="text-dark-gray fs-12 fw-400">Approved</span>
                                                    <span className="text-success fs-16 fw-700">{formatAmount(item.data.approved, currency?.code, currency?.symbol, currency?.locale)}</span>
                                                </div>
                                                <div className="grouped-earning__card-content-item app-sub-card">
                                                    <span className="text-dark-gray fs-12 fw-400">Pending</span>
                                                    <span className="text-dark fs-16 fw-700">{formatAmount(item.data.pending, currency?.code, currency?.symbol, currency?.locale)}</span>
                                                </div>
                                                <div className="grouped-earning__card-content-item app-sub-card">
                                                    <span className="text-dark-gray fs-12 fw-400">Rejected</span>
                                                    <span className="text-dark fs-16 fw-700">{formatAmount(item.data.rejected, currency?.code, currency?.symbol, currency?.locale)}</span>
                                                </div>
                                            </div>
                                        </div>
                                    )
                                })
                            }
                        </>
                    )
                }
            </div>
            {
                (remainingItemsCount > 0) && !showAll &&
                <div className="grouped-earnings__remaining-collapsed-items">
                    {
                        remainingItems.map((item, stackIndex) => {
                            return (
                                <div
                                    className="grouped-earning__card grouped-earning__card--remaining app-card p-075"
                                    key={item.key}
                                    style={{ '--remaining-stack-index': stackIndex+1} as React.CSSProperties}
                                >
                                    <span className="text-dark fs-16 fw-400">{item.label}</span>
                                </div>
                            )
                        })
                    }
                </div>
            }
            {
                remainingItemsCount > 0 &&
                <div className="d-flex justify-content-center align-items-center gap-2">
                    <button className="btn btn-outline-primary show-more-button" onClick={() => setShowAll(!showAll)}>
                        {
                            !(showAll) ? <span>Show More <ChevronDownIcon /></span> : <span>Show Less <ChevronUpIcon /></span>
                        }
                    </button>
                </div>
            }
        </div>
    )
}

export default GroupedEarnings